import { useEffect, useState } from 'react';

import { StudioSpaceAri } from '@atlassian/ari/studio/space';

import { useExperimentalQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalQuery.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { getAutomationStack } from '../utils/AutomationAPIUtilities.ts';

type StudioContextResponse = {
	/**
	 * e.g. `ari:cloud:studio::space/{workspaceId}/{spaceId}`
	 */
	spaceARI?: string | null;
};

type StudioContextData = {
	studioSpaceAri: StudioSpaceAri | null;
};

type UseStudioSpaceAriResponse = {
	studioSpaceAri: StudioSpaceAri | null;
	isLoading: boolean;
};

type useStudioSpaceAriProps = {
	/**
	 * Skips the fetch entirely. Use this to defer the network call.
	 */
	skip?: boolean;
};

/**
 * Returns the Studio space ARI for the current Confluence site.
 */
export const useStudioSpaceAri = ({
	skip = false,
}: useStudioSpaceAriProps = {}): UseStudioSpaceAriResponse => {
	const { cloudId, environment } = useSessionData();
	const [automationStack, setAutomationStack] = useState<string | null | undefined>(undefined);

	useEffect(() => {
		if (skip) {
			return;
		}

		let cancelled = false;
		void getAutomationStack(environment)
			.then((stack) => {
				if (!cancelled) {
					setAutomationStack(stack);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setAutomationStack(null);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [environment, skip]);

	const isStackSettled = automationStack !== undefined;
	const shouldRunQuery = !skip && Boolean(cloudId) && Boolean(automationStack);

	const { data, loading } = useExperimentalQuery<
		StudioContextData,
		Record<string, never>,
		StudioContextResponse
	>({
		url: `/gateway/api/automation/internal-api/confluence/${cloudId}/${automationStack}/rest/v1/studioContext`,
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
		},
		credentials: 'include',
		parseResult: (response) => {
			const spaceAri = response?.spaceARI;
			return {
				studioSpaceAri:
					typeof spaceAri === 'string' && StudioSpaceAri.check(spaceAri)
						? StudioSpaceAri.parse(spaceAri)
						: null,
			};
		},
		skip: !shouldRunQuery,
		fetchPolicy: 'cache-first',
	});

	return {
		studioSpaceAri: data?.studioSpaceAri ?? null,
		// Loading until we have a definitive answer: while the automation stack is still resolving, or
		// while the studioContext query for a valid stack is in flight. Once the stack settles to no
		// value (or is skipped) there is nothing to fetch, so this is false and the ARI is `null`.
		isLoading: !skip && (!isStackSettled || (shouldRunQuery && loading)),
	};
};
